import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Collapse, Select } from 'antd';
import { IconBackground, InfiniteScroll, Spin } from 'common/components';
import { EColor } from 'common/const/color.enum';
import { mapToMenuWithDivider, showSuccessMessage } from 'common/helpers';
import { LIST_LIMIT_0 } from 'common/config';
import { ArrowDownShort, ArrowRightLong, ArrowUpShort, Delivery } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { IContract } from 'entities/Contract/Contract.models';
import { deliveryInDaysOptions } from 'entities/Delivery/Delivery.const';
import { getDeliveryOptionValue } from 'entities/Delivery/Delivery.helper';
import {
  createDelivery,
  deleteDelivery,
  getDeliveryBuyerCities,
  getDeliveryList,
  selectDeliveryBuyerCities,
  selectDeliveryList,
  updateDelivery,
} from 'entities/Delivery/Delivery.slice';
import { getStockList, selectStockList } from 'entities/Stock/Stock.slice';

interface IComponentProps {
  header: ReactNode;
  contract: IContract | null;
}

export const SettingsContractDeliveryTimeListTab: FC<IComponentProps> = ({ header, contract }) => {
  const dispatch = useAppDispatch();
  // delivery
  const deliveryList = useAppSelector(selectDeliveryList);
  const deliveryBuyerCities = useAppSelector(selectDeliveryBuyerCities);
  // stock
  const stockList = useAppSelector(selectStockList);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);

  const successMessage = 'Срок доставки успешно изменен.';

  const onCreateDelivery = (city: string, stockId: number, deliveryInDays: number): void => {
    if (!contract) {
      return;
    }

    dispatch(
      createDelivery({
        contractId: contract.id,
        city,
        stockId,
        deliveryInDays,
        onSuccess: () => {
          showSuccessMessage(successMessage);
        },
      }),
    );
  };

  const onUpdateDelivery = (city: string, stockId: number, deliveryInDays: number): void => {
    if (!contract) {
      return;
    }

    dispatch(
      updateDelivery({
        contractId: contract.id,
        city,
        stockId,
        deliveryInDays,
        onSuccess: () => {
          showSuccessMessage(successMessage);
        },
      }),
    );
  };

  const onDeletedDelivery = (city: string, stockId: number): void => {
    if (!contract) {
      return;
    }

    dispatch(
      deleteDelivery({
        contractId: contract.id,
        city,
        stockId,
        onSuccess: () => {
          showSuccessMessage(successMessage);
        },
      }),
    );
  };

  useEffect(() => {
    const fetch = async () => {
      setFetchLoading(true);

      if (contract) {
        await dispatch(getStockList({ limit: LIST_LIMIT_0 }));
        await dispatch(getDeliveryBuyerCities({ buyerId: contract.buyerId }));
        await dispatch(getDeliveryList({ contractId: contract.id, limit: LIST_LIMIT_0 }));
      }

      setFetchLoading(false);
    };

    fetch();
  }, [contract?.id]);

  return (
    <InfiniteScroll>
      {header}

      <div>
        <Spin spinning={fetchLoading}>
          <Collapse
            accordion
            className="mb-19 collapse"
            expandIconPosition="end"
            expandIcon={({ isActive }) =>
              isActive ? (
                <ArrowUpShort className="icon-arrow-up-short-dark-grey" />
              ) : (
                <ArrowDownShort className="icon-arrow-down-short-dark-grey" />
              )
            }
            items={stockList.map((stock) => {
              return {
                key: stock.id,
                label: (
                  <div className="contract-delivery-time-list-settings__collapse-header">
                    <IconBackground content={<Delivery className="icon-delivery-bright-green" />} color={EColor.PaleGreen} />

                    <div style={{ flex: 1 }}>
                      <div className="text-controls-accent mb-2">{stock.name}</div>

                      <span className="text-tag color-dark-grey">{stock.address}</span>
                    </div>
                  </div>
                ),
                children: deliveryBuyerCities.map((city, index) => {
                  const delivery = deliveryList.find((item) => item.stockId === stock.id && item.city === city);

                  return (
                    <div key={index} className="collapse__content-row contract-delivery-time-list-settings__collapse-content-row">
                      <div className="contract-delivery-time-list-settings__collapse-content-row-container">
                        <IconBackground
                          content={<ArrowRightLong className="icon-arrow-right-long-dark-grey" />}
                          color={EColor.LightGrey}
                        />

                        <span className="text-controls-accent" style={{ flex: 1 }}>
                          {city}
                        </span>
                      </div>

                      <div
                        className={`contract-delivery-time-list-settings__collapse-content-row-selector ${
                          delivery ? 'bright-green' : 'light-grey'
                        }`}
                      >
                        <Delivery className="contract-delivery-time-list-settings__collapse-content-row-selector-icon" />

                        <Select
                          popupClassName="redesign contract-delivery-time-list-settings__collapse-content-row-selector-select"
                          virtual={false}
                          popupMatchSelectWidth={false}
                          suffixIcon={null}
                          options={mapToMenuWithDivider(deliveryInDaysOptions)}
                          value={delivery ? getDeliveryOptionValue(delivery.deliveryInDays) : null}
                          onChange={(value) => {
                            if (!delivery && value) {
                              onCreateDelivery(city, stock.id, value);
                            }

                            if (delivery && value) {
                              onUpdateDelivery(city, stock.id, value);
                            }

                            if (delivery && !value) {
                              onDeletedDelivery(city, stock.id);
                            }
                          }}
                        />
                      </div>
                    </div>
                  );
                }),
              };
            })}
          />
        </Spin>
      </div>
    </InfiniteScroll>
  );
};
